/**
 * Utility to convert PlaceCardData to PostLocationPayload format
 * 
 * Handles conversion of place data from list view cards to itinerary post location format
 * Supports both Google Places and user-created places
 */

import type { PostLocationPayload } from '@/utils/itinerary/types';
import type { PlaceCardData } from '@/types/listViewTypes';
import type { RawLocationData } from '@/utils/postTypes';
import { fetchWebsiteIfMissing } from '@/utils/websiteUtils';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import { appendLanguageCodeForGooglePlacesUpsert } from '@/utils/placesLanguageCode';
import { isStayPlace } from '@/utils/placeCategoryDisplay';

/**
 * Determine category/gt_category from place data
 * 
 * @param place - Place card data
 * @returns Category string ('eat', 'do', 'stay', 'flight') or 'do' as default
 */
function determineCategory(place: PlaceCardData): string {
  // Try to infer from place data
  // Check locationType for common patterns
  if (place.locationType) {
    const lowerType = place.locationType.toLowerCase();
    if (lowerType.includes('restaurant') || lowerType.includes('cafe') || lowerType.includes('food')) {
      return 'eat';
    }
    if (lowerType.includes('hotel') || lowerType.includes('accommodation') || lowerType.includes('stay')) {
      return 'stay';
    }
    if (lowerType.includes('flight') || lowerType.includes('airport')) {
      return 'flight';
    }
  }
  
  // Check cuisine field
  if (place.cuisine) {
    return 'eat';
  }
  
  // Default to 'do' (activities)
  return 'do';
}

/** Whether a saved place is somewhere to stay; trips keep these unscheduled until put on a day. */
export function isStayPlaceCard(place: PlaceCardData): boolean {
  return determineCategory(place) === 'stay' || isStayPlace({ google_types: place.googleTypes });
}

/**
 * Convert PlaceCardData to PostLocationPayload format
 * 
 * Handles both Google Places (with google_place_id) and user-created places
 * 
 * @param place - Place card data from list view
 * @param dayIndex - Day index for itinerary (default: 0)
 * @param sequenceIndex - Sequence index within day (default: 0)
 * @param cityAddress - Optional city address for website fetching
 * @returns PostLocationPayload object ready for API
 */
export async function convertPlaceToPostLocation(
  place: PlaceCardData,
  dayIndex: number = 0,
  sequenceIndex: number = 0,
  cityAddress?: string
): Promise<PostLocationPayload> {
  // Determine if this is a Google Place
  const googlePlaceId = place.googlePlaceId || place.placeId || null;
  const isGooglePlace = !!googlePlaceId;
  
  // Determine category
  const category = determineCategory(place);
  
  if (isGooglePlace && googlePlaceId) {
    const locationMongoId = isMongoObjectIdString(place.locationId) ? place.locationId : undefined;

    // Google Place format
    let locationData: RawLocationData = {
      ...(locationMongoId ? { _id: locationMongoId } : {}),
      name: place.title,
      google_place_id: googlePlaceId,
      address: place.address,
      gt_category: category,
      ...(place.website ? { website: place.website } : {}),
      ...(place.latitude != null && place.longitude != null
        ? { geoposition: { type: 'Point' as const, coordinates: [place.longitude, place.latitude] as [number, number] } }
        : {}),
    };
    
    // Fetch website from Google if missing
    locationData = (await fetchWebsiteIfMissing(locationData, place.title, cityAddress)) || locationData;

    appendLanguageCodeForGooglePlacesUpsert(locationData as unknown as Record<string, unknown>);

    return {
      body: place.title, // Required: use title as body
      itinerary_day_index: dayIndex, // Required
      itinerary_day_order_index: sequenceIndex, // Required
      location: locationData, // Required for Google Places
      location_id: locationMongoId ?? undefined, // Send Mongo id when already resolved
      image_urls: place.imageUrls || [], // Optional
      notes: place.description || place.notes || '', // Optional
      budget: place.cost ? (typeof place.cost === 'number' ? place.cost : parseFloat(String(place.cost))) : null, // Optional
      timeRange: undefined, // Optional, not available in PlaceCardData
    };
  } else {
    // User-created place format
    const locationData: RawLocationData | undefined = place.address ? {
      name: place.title,
      address: place.address,
      // postPresentationNormalizer checks a3_intl_address first in the fallback chain
      a3_intl_address: place.address,
      gt_category: category,
      ...(place.latitude != null && place.longitude != null ? {
        geoposition: {
          coordinates: [place.longitude, place.latitude] as [number, number],
        },
      } : {}),
    } : undefined;
    
    return {
      body: place.title, // Required
      itinerary_day_index: dayIndex, // Required
      itinerary_day_order_index: sequenceIndex, // Required
      address: place.address || '', // Required for user-created
      is_user_created: true, // Required for user-created
      location: locationData, // Optional: include if address available
      image_urls: place.imageUrls || [], // Optional
      notes: place.description || place.notes || '', // Optional
      budget: place.cost ? (typeof place.cost === 'number' ? place.cost : parseFloat(String(place.cost))) : null, // Optional
      timeRange: undefined, // Optional, not available in PlaceCardData
    };
  }
}

